import React, { useState, useEffect, useRef } from 'react'
import { readFromOPFS } from '../../lib/storage/opfs'
import { PillButton } from '../actions/PillButton'
import { BadgeChip } from '../editorial/BadgeChip'
import type { FileRecord } from '../../lib/storage/types'

export interface ComparisonModalProps {
  record: FileRecord
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export const ComparisonModal: React.FC<ComparisonModalProps> = ({
  record,
  onClose,
}) => {
  const [originalUrl, setOriginalUrl] = useState<string | null>(null)
  const [optimizedUrl, setOptimizedUrl] = useState<string | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null)
  const [sliderPos, setSliderPos] = useState(50)
  const [loading, setLoading] = useState(true)
  const [isZoomed, setIsZoomed] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)

  const defaultPreferred =
    record.results?.find((r) => r.format === 'avif') ||
    record.results?.find((r) => r.format === 'webp') ||
    record.results?.[0] ||
    null

  const selectedResult =
    (selectedFormat ? record.results?.find((r) => r.format === selectedFormat) : null) ||
    defaultPreferred

  const isVideo = record.category === 'video'
  const [isPlaying, setIsPlaying] = useState(false)
  const origVideoRef = useRef<HTMLVideoElement>(null)
  const optVideoRef = useRef<HTMLVideoElement>(null)

  const togglePlay = () => {
    if (!origVideoRef.current || !optVideoRef.current) return
    if (isPlaying) {
      origVideoRef.current.pause()
      optVideoRef.current.pause()
      setIsPlaying(false)
    } else {
      origVideoRef.current.play().catch(() => {})
      optVideoRef.current.play().catch(() => {})
      setIsPlaying(true)
    }
  }

  // Load original and optimized blobs from OPFS
  useEffect(() => {
    let active = true
    let origUrl: string | null = null
    let optUrl: string | null = null

    async function loadBuffers() {
      setLoading(true)
      try {
        const origBuffer = await readFromOPFS(record.opfsPath)
        if (origBuffer && active) {
          const blob = new Blob([origBuffer], { type: record.mimeType })
          origUrl = URL.createObjectURL(blob)
          setOriginalUrl(origUrl)
        }

        if (selectedResult && active) {
          const optBuffer = await readFromOPFS(selectedResult.opfsPath)
          if (optBuffer && active) {
            const blob = new Blob([optBuffer])
            optUrl = URL.createObjectURL(blob)
            setOptimizedUrl(optUrl)
          }
        }
      } catch (err) {
        console.error('Error loading comparison buffers:', err)
      } finally {
        if (active) setLoading(false)
      }
    }

    loadBuffers()

    return () => {
      active = false
      if (origUrl) URL.revokeObjectURL(origUrl)
      if (optUrl) URL.revokeObjectURL(optUrl)
    }
  }, [record.opfsPath, record.mimeType, selectedResult])

  const handlePointerDown = () => {
    isDraggingRef.current = true
  }

  const handlePointerUp = () => {
    isDraggingRef.current = false
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const percent = Math.round((x / rect.width) * 100)
    setSliderPos(percent)
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(5, 7, 11, 0.88)',
        backdropFilter: 'blur(20px)',
        padding: '24px',
      }}
      onPointerUp={handlePointerUp}
    >
      <div
        style={{
          width: 'min(1100px, 95vw)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#070A11',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '24px',
          boxShadow: '0 24px 60px -10px rgba(0, 0, 0, 0.8)',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <i className="pi pi-eye" style={{ color: 'var(--c-accent-electric, #38BFFF)' }} />
            <div>
              <h3
                style={{
                  fontFamily: "var(--font-display, 'Unbounded', sans-serif)",
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#ffffff',
                }}
              >
                Inspección de Fidelidad Perceptual
              </h3>
              <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                {record.name}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Format selector chips */}
            {record.results && record.results.length > 1 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                {record.results.map((res) => (
                  <button
                    key={res.filename}
                    type="button"
                    onClick={() => setSelectedFormat(res.format)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11.5px',
                      fontFamily: "var(--font-data, 'Space Grotesk', monospace)",
                      fontWeight: 600,
                      background:
                        selectedResult?.filename === res.filename
                          ? 'var(--c-accent-electric, #38BFFF)'
                          : 'rgba(255, 255, 255, 0.06)',
                      color:
                        selectedResult?.filename === res.filename ? '#0B1230' : '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {res.label}
                  </button>
                ))}
              </div>
            )}

            <PillButton
              label={isZoomed ? 'Ajustar' : 'Zoom 1:1'}
              variant="outline"
              size="sm"
              icon={isZoomed ? 'pi-search-minus' : 'pi-search-plus'}
              onClick={() => setIsZoomed(!isZoomed)}
            />

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: 'none',
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                cursor: 'pointer',
              }}
            >
              <i className="pi pi-times" />
            </button>
          </div>
        </div>

        {/* Viewer Viewport */}
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            flex: 1,
            minHeight: '420px',
            maxHeight: '68vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'radial-gradient(circle at center, rgba(255, 255, 255, 0.02) 0%, #030508 100%)',
            overflow: isZoomed ? 'auto' : 'hidden',
            userSelect: 'none',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
        >
          {loading && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '24px' }} />
              <span>Cargando buffers desde OPFS...</span>
            </div>
          )}

          {!loading && originalUrl && optimizedUrl && (
            <div
              style={{
                position: 'relative',
                width: isZoomed ? 'auto' : '100%',
                height: isZoomed ? 'auto' : '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* After / Optimized Media (Base layer) */}
              {isVideo ? (
                <video
                  ref={optVideoRef}
                  src={optimizedUrl}
                  playsInline
                  loop
                  muted
                  style={{
                    display: 'block',
                    maxWidth: isZoomed ? 'none' : '100%',
                    maxHeight: isZoomed ? 'none' : '62vh',
                    objectFit: 'contain',
                  }}
                  onTimeUpdate={() => {
                    if (origVideoRef.current && optVideoRef.current) {
                      if (Math.abs(origVideoRef.current.currentTime - optVideoRef.current.currentTime) > 0.1) {
                        origVideoRef.current.currentTime = optVideoRef.current.currentTime
                      }
                    }
                  }}
                />
              ) : (
                <img
                  src={optimizedUrl}
                  alt="Optimizado"
                  style={{
                    display: 'block',
                    maxWidth: isZoomed ? 'none' : '100%',
                    maxHeight: isZoomed ? 'none' : '62vh',
                    objectFit: 'contain',
                  }}
                />
              )}

              {/* Before / Original Media (Clipped layer) */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflow: 'hidden',
                  clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isVideo ? (
                  <video
                    ref={origVideoRef}
                    src={originalUrl}
                    playsInline
                    loop
                    muted
                    style={{
                      display: 'block',
                      maxWidth: isZoomed ? 'none' : '100%',
                      maxHeight: isZoomed ? 'none' : '62vh',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <img
                    src={originalUrl}
                    alt="Original"
                    style={{
                      display: 'block',
                      maxWidth: isZoomed ? 'none' : '100%',
                      maxHeight: isZoomed ? 'none' : '62vh',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </div>

              {/* Split Slider Divider Handle */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${sliderPos}%`,
                  width: '2px',
                  background: '#ffffff',
                  boxShadow: '0 0 12px rgba(56, 191, 255, 0.8)',
                  cursor: 'ew-resize',
                  zIndex: 10,
                  transform: 'translateX(-50%)',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#0B1230',
                    border: '2px solid #ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.6)',
                  }}
                >
                  <i
                    className="pi pi-chevron-left"
                    style={{ fontSize: '10px', color: '#ffffff' }}
                  />
                  <i
                    className="pi pi-chevron-right"
                    style={{ fontSize: '10px', color: '#ffffff' }}
                  />
                </div>
              </div>

              {/* Floating Comparison Labels */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  left: '16px',
                  zIndex: 20,
                  pointerEvents: 'none',
                }}
              >
                <BadgeChip variant="default">
                  ORIGINAL: {formatBytes(record.originalSize)}
                </BadgeChip>
              </div>

              <div
                style={{
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  zIndex: 20,
                  pointerEvents: 'none',
                }}
              >
                {selectedResult && (
                  <BadgeChip variant="pulse">
                    {selectedResult.label}: {formatBytes(selectedResult.size)} (-
                    {selectedResult.savingsPercent}%)
                  </BadgeChip>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'rgba(255, 255, 255, 0.01)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isVideo && (
              <PillButton
                label={isPlaying ? 'Pausar Video' : 'Reproducir Video'}
                variant="solid"
                size="sm"
                icon={isPlaying ? 'pi-pause' : 'pi-play'}
                onClick={togglePlay}
              />
            )}
            <span style={{ fontSize: '12.5px', color: 'rgba(255, 255, 255, 0.5)' }}>
              {isVideo
                ? 'Videos reproducidos en sincronía exacta. Arrastra el divisor central.'
                : 'Arrastra el divisor central para comparar textura, nitidez y gradientes.'}
            </span>
          </div>
          <PillButton label="Cerrar Inspección" variant="ghost" size="sm" onClick={onClose} />
        </div>
      </div>
    </div>
  )
}
